import React from 'react';
import {
  RssIcon,
  UserIcon,
  ClockIcon,
  ChatBubbleLeftIcon,
  HeartIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  SparklesIcon,
  ArrowPathIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  EyeIcon,
  HandThumbUpIcon,
  ChatBubbleBottomCenterTextIcon,
  BellIcon
} from '@heroicons/react/24/outline';

export default function ActivityFeedHelp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full mb-6">
            <RssIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Activity Feed Guide</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Your live news ticker for staying connected with team activities and managing your day
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-100">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <SparklesIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">What is the Activity Feed?</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                Think of it as your team's social media feed, but for work. It shows real-time updates about posts, comments, likes, messages, and task activities. Everything happens automatically, so you're always in the loop without checking multiple places.
              </p>
            </div>
          </div>
        </div>

        {/* Feed Layout Diagram */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Activity Feed Layout</h2>

          {/* Desktop Layout */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <ComputerDesktopIcon className="w-5 h-5 mr-2 text-green-600" />
              How It Looks on Desktop
            </h3>
            <div className="bg-gray-50 rounded-xl p-6 border-2 border-dashed border-gray-300">
              <div className="space-y-4">
                {/* User Info */}
                <div className="bg-blue-100 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center space-x-3">
                    <UserIcon className="w-5 h-5 text-blue-600" />
                    <div className="space-y-1">
                      <div className="w-24 h-3 bg-blue-300 rounded"></div>
                      <div className="w-32 h-2 bg-blue-200 rounded"></div>
                    </div>
                  </div>
                  <p className="text-xs text-blue-700 mt-2 font-medium">Your Profile Info</p>
                </div>

                {/* Current Task Card */}
                <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <ClockIcon className="w-5 h-5 text-purple-600" />
                      <div className="w-20 h-3 bg-purple-300 rounded"></div>
                    </div>
                    <div className="w-12 h-6 bg-red-200 rounded-full"></div>
                  </div>
                  <div className="w-full h-2 bg-purple-200 rounded-full mb-2">
                    <div className="w-3/4 h-2 bg-purple-400 rounded-full"></div>
                  </div>
                  <div className="flex space-x-2">
                    <div className="w-16 h-6 bg-purple-300 rounded"></div>
                    <div className="w-16 h-6 bg-purple-300 rounded"></div>
                  </div>
                  <p className="text-xs text-purple-700 mt-2 font-medium">Current Task Card</p>
                </div>

                {/* Activity Items */}
                <div className="space-y-2">
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <div className="flex items-center space-x-2 mb-1">
                      <PaperAirplaneIcon className="w-4 h-4 text-blue-600" />
                      <div className="w-16 h-2 bg-blue-300 rounded"></div>
                    </div>
                    <div className="w-full h-2 bg-blue-200 rounded"></div>
                    <p className="text-xs text-blue-700 mt-1 font-medium">Team Post</p>
                  </div>

                  <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                    <div className="flex items-center space-x-2 mb-1">
                      <HeartIcon className="w-4 h-4 text-green-600" />
                      <div className="w-12 h-2 bg-green-300 rounded"></div>
                    </div>
                    <p className="text-xs text-green-700 mt-1 font-medium">Like Notification</p>
                  </div>

                  <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                    <div className="flex items-center space-x-2 mb-1">
                      <ChatBubbleLeftIcon className="w-4 h-4 text-yellow-600" />
                      <div className="w-14 h-2 bg-yellow-300 rounded"></div>
                    </div>
                    <p className="text-xs text-yellow-700 mt-1 font-medium">Comment</p>
                  </div>

                  <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
                    <div className="flex items-center space-x-2 mb-1">
                      <ChatBubbleBottomCenterTextIcon className="w-4 h-4 text-indigo-600" />
                      <div className="w-18 h-2 bg-indigo-300 rounded"></div>
                    </div>
                    <p className="text-xs text-indigo-700 mt-1 font-medium">Direct Message</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Layout */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <DevicePhoneMobileIcon className="w-5 h-5 mr-2 text-emerald-600" />
              Mobile View
            </h3>
            <div className="bg-gray-50 rounded-xl p-6 border-2 border-dashed border-gray-300 max-w-xs mx-auto">
              <div className="space-y-3">
                <div className="bg-blue-100 rounded p-2 border border-blue-200">
                  <div className="w-12 h-2 bg-blue-300 rounded mb-1"></div>
                  <div className="w-16 h-2 bg-blue-200 rounded"></div>
                  <p className="text-xs text-blue-700 mt-1 font-medium">Profile</p>
                </div>

                <div className="bg-purple-100 rounded p-2 border border-purple-200">
                  <div className="w-full h-3 bg-purple-300 rounded mb-1"></div>
                  <div className="w-3/4 h-2 bg-purple-200 rounded"></div>
                  <p className="text-xs text-purple-700 mt-1 font-medium">Task</p>
                </div>

                <div className="space-y-1">
                  <div className="bg-blue-50 rounded p-2 border border-blue-200">
                    <div className="w-8 h-2 bg-blue-300 rounded"></div>
                  </div>
                  <div className="bg-green-50 rounded p-2 border border-green-200">
                    <div className="w-6 h-2 bg-green-300 rounded"></div>
                  </div>
                  <div className="bg-yellow-50 rounded p-2 border border-yellow-200">
                    <div className="w-10 h-2 bg-yellow-300 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Activity Types */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <PaperAirplaneIcon className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Team Posts</h3>
            <p className="text-gray-600">News, updates, and announcements from your team members</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <HeartIcon className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Likes & Reactions</h3>
            <p className="text-gray-600">See when colleagues appreciate posts and updates</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
              <ChatBubbleLeftIcon className="w-6 h-6 text-yellow-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Comments</h3>
            <p className="text-gray-600">Conversations and discussions on team posts</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
              <ChatBubbleBottomCenterTextIcon className="w-6 h-6 text-indigo-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Direct Messages</h3>
            <p className="text-gray-600">Private conversations and quick chats</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <CheckCircleIcon className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Task Updates</h3>
            <p className="text-gray-600">Clock-ins, approvals, and work activity notifications</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
              <ExclamationCircleIcon className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Pending Actions</h3>
            <p className="text-gray-600">Items waiting for your attention or approval</p>
          </div>
        </div>

        {/* Task Spotlight */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <ClockIcon className="w-6 h-6 mr-3 text-purple-600" />
            Your Current Task Spotlight
          </h2>

          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <ClockIcon className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Task Name</h3>
                  <p className="text-sm text-gray-600">What you need to do</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">High Priority</span>
                <span className="text-sm text-gray-500">2:30 PM</span>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Progress</span>
                <span>75%</span>
              </div>
              <div className="w-full bg-purple-200 rounded-full h-2">
                <div className="bg-purple-500 h-2 rounded-full" style={{width: '75%'}}></div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                View Team Tasks
              </button>
              <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                Start Chat
              </button>
            </div>
          </div>

          <div className="mt-6 grid md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-red-600 font-bold text-sm">H</span>
              </div>
              <p className="text-sm font-medium text-gray-900">High Priority</p>
              <p className="text-xs text-gray-600">Red progress bar</p>
            </div>
            <div className="text-center">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-yellow-600 font-bold text-sm">M</span>
              </div>
              <p className="text-sm font-medium text-gray-900">Medium Priority</p>
              <p className="text-xs text-gray-600">Yellow progress bar</p>
            </div>
            <div className="text-center">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-green-600 font-bold text-sm">L</span>
              </div>
              <p className="text-sm font-medium text-gray-900">Low Priority</p>
              <p className="text-xs text-gray-600">Green progress bar</p>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-8 text-white mb-8">
          <h2 className="text-2xl font-bold mb-6">Why It Matters</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start space-x-3">
              <ArrowPathIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Real-Time Updates</h3>
                <p className="text-green-100">Never miss important updates - everything appears instantly</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <EyeIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Stay Focused</h3>
                <p className="text-green-100">Your current task is always front and center</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <HandThumbUpIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Team Connection</h3>
                <p className="text-green-100">Like, comment, and chat without leaving your dashboard</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <BellIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Personalized Feed</h3>
                <p className="text-green-100">Only see what's relevant to your role and responsibilities</p>
              </div>
            </div>
          </div>
        </div>

        {/* Getting Started */}
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <SparklesIcon className="w-6 h-6 mr-3 text-emerald-500" />
            Getting Started
          </h2>
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-bold text-sm">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Open Your Dashboard</h3>
                <p className="text-gray-600">The Activity Feed loads automatically when you log in</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-bold text-sm">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Check Your Task</h3>
                <p className="text-gray-600">Review your current task card at the top of the feed</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-bold text-sm">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Scroll Through Updates</h3>
                <p className="text-gray-600">Browse recent posts, likes, comments, and messages</p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-bold text-sm">4</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Interact & Engage</h3>
                <p className="text-gray-600">Like posts, reply to messages, and join conversations</p>
              </div>
            </div>
          </div>

          <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start space-x-3">
              <ArrowPathIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-800">Auto-Refresh</h3>
                <p className="text-blue-700 text-sm">
                  The feed updates automatically, but if something doesn't load, just refresh the page.
                  Everything reconnects smoothly most of the time!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}